import type { AnswerCard, AssistCard } from './assist.types'

/**
 * The id of the suggestion a question came from, if it came from one.
 *
 * Asking again by id keeps the question on the route it was already sorted
 * onto. Re-sending the text puts it back through classification — another
 * model call, and a chance of landing on a different route than the one the
 * reader pressed.
 *
 * Read off the cards rather than remembered separately: the suggestion that
 * raised the question is still in the list, and a second copy of the mapping
 * would be one more thing to keep in step.
 *
 * @param cards Every card on screen, suggestions included.
 * @param question The refused question, verbatim.
 * @returns The suggestion id, or null when the reader typed the question.
 */
export function suggestionIdForQuestion(cards: AssistCard[], question: string): string | null {
  const wanted = question.trim()
  if (!wanted) return null

  for (const card of cards) {
    if (card.type !== 'query_suggestion') continue
    for (const suggestion of card.suggestions) {
      if (suggestion.text.trim() === wanted) return suggestion.id
    }
  }
  return null
}

/** What the reader has in place now, as far as Backlog is concerned. */
export interface BacklogPrerequisites {
  /** A working grant, with an account behind it. */
  connected: boolean
  /** The meeting names a Backlog project. */
  hasProject: boolean
}

/**
 * The refusals this screen can do something about, and what clears each one.
 *
 * Deliberately a short list. `backlog_unavailable` is a server that has the
 * lookup switched off and `backlog_scope_failed` is a call that did not come
 * back — neither is fixed by connecting an account or naming a project, so
 * re-asking would only produce the same sentence a second time.
 *
 * `backlog_not_meeting_member` is the loose one: what actually clears it is
 * being added to the project, which does not happen here. It is listed
 * because pointing the meeting at a *different* project does clear it, and
 * that is a change the reader makes from this panel. Retrying is only ever
 * triggered by such a change, so this cannot fire on its own.
 */
const CLEARED_BY: Readonly<Record<string, (prerequisites: BacklogPrerequisites) => boolean>> = {
  backlog_not_connected: (prerequisites) => prerequisites.connected,
  backlog_no_meeting_project: (prerequisites) => prerequisites.hasProject,
  backlog_not_meeting_member: (prerequisites) =>
    prerequisites.connected && prerequisites.hasProject,
  // The meeting's project exists but its tickets were never synced. Cleared by
  // the same move as the refusal above — pick a project that was.
  backlog_project_not_synced: (prerequisites) =>
    prerequisites.connected && prerequisites.hasProject,
}

/**
 * Which of the listed refusals a failed card is, or null for anything else.
 *
 * @param card A card whose status is already known to be `failed`.
 */
function blockerOf(card: AnswerCard): string | null {
  const error = card.error
  if (!error) return null

  const reason = error.reason?.trim()
  if (reason) return reason in CLEARED_BY ? reason : null

  // No `reason` at all means a service that predates the field rather than a
  // refusal without one. On the Backlog route `not_supported` was almost
  // always the unconnected account, which is the same reading
  // `resolveBacklogRefusal` makes when it decides to offer the Settings link.
  return error.code === 'not_supported' && card.route === 'backlog' ? 'backlog_not_connected' : null
}

/**
 * The questions that were turned down for something the reader has since put
 * right, and are therefore worth asking again (BRAI-689 follow-up).
 *
 * A reader who asks about a ticket before connecting Backlog gets a refusal,
 * goes and connects, and then has to type the question out a second time to
 * find out it would have worked. These are the questions that spares them.
 *
 * Only `failed` cards: a refusal is a failure, and a card that answered is one
 * whose question has already been spent.
 *
 * @param cards The panel's cards, in the order they are drawn.
 * @param prerequisites What is in place now.
 * @returns The cards to ask again, oldest first, so the answers come back in
 *   the order the reader asked the questions.
 */
export function refusalsClearedBy(
  cards: AssistCard[],
  prerequisites: BacklogPrerequisites
): AnswerCard[] {
  const cleared: AnswerCard[] = []
  for (const card of cards) {
    if (card.type !== 'answer' || card.status !== 'failed') continue
    if (!card.question.trim()) continue
    const blocker = blockerOf(card)
    if (blocker && CLEARED_BY[blocker](prerequisites)) cleared.push(card)
  }
  return cleared
}
